/**
 * Intranet API client.
 * Points to the dedicated Intranet API Gateway.
 */

import { useAuthStore, getCurrentSession, getTokensFromSession } from '@ipsoftsrl/frontend-auth';

const INTRANET_API_BASE_URL = import.meta.env.VITE_INTRANET_API_BASE_URL || '/api/portals/intranet';

async function ensureFreshToken(): Promise<string | null> {
  try {
    const session = await getCurrentSession();
    if (session) {
      const tokens = getTokensFromSession(session);
      useAuthStore.getState().setTokens(tokens.accessToken, tokens.idToken);
      return tokens.idToken;
    }
  } catch {}
  return useAuthStore.getState().idToken;
}

async function intranetRequest<T>(endpoint: string, options: RequestInit = {}): Promise<{ data: T }> {
  const url = `${INTRANET_API_BASE_URL}${endpoint}`;
  const token = await ensureFreshToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> || {}),
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorBody.message || `HTTP ${response.status}`);
  }

  const data = await response.json();
  return { data };
}

export const intranetApi = {
  get: <T>(endpoint: string) => intranetRequest<T>(endpoint, { method: 'GET' }),
  post: <T>(endpoint: string, body?: unknown) =>
    intranetRequest<T>(endpoint, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(endpoint: string, body?: unknown) =>
    intranetRequest<T>(endpoint, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(endpoint: string) => intranetRequest<T>(endpoint, { method: 'DELETE' }),
};
